import { motion } from 'motion/react';
import { ArrowLeft, Shield, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { db, doc, onSnapshot, Timestamp } from '../firebase';

interface PrivacyPolicyScreenProps {
  onBack: () => void;
}

interface PolicyData {
  name: string;
  content: string;
  updatedAt: Timestamp | Date | string | number | null;
}

export function PrivacyPolicyScreen({ onBack }: PrivacyPolicyScreenProps) {
  const [policy, setPolicy] = useState<PolicyData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'app_settings', 'privacy-policy'),
      (snapshot) => {
        if (snapshot.exists()) {
          setPolicy(snapshot.data() as PolicyData);
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const formatDate = (
    timestamp: Timestamp | Date | string | number | null | undefined
  ) => {
    if (!timestamp) return '';

    const date =
      timestamp instanceof Timestamp
        ? timestamp.toDate()
        : new Date(timestamp as Date | string | number);

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex min-h-screen flex-col bg-zinc-50 transition-colors duration-300 dark:bg-black"
    >
      <header className="fixed top-0 right-0 left-0 z-50 border-b border-zinc-100 bg-white/80 p-6 backdrop-blur-xl dark:border-zinc-800 dark:bg-zinc-950/80">
        <div className="mx-auto flex max-w-2xl items-center gap-4">
          <button
            onClick={onBack}
            className="rounded-xl border border-zinc-100 bg-white p-3 text-zinc-400 shadow-sm transition-colors hover:text-zinc-900 dark:border-zinc-800 dark:bg-zinc-900 dark:hover:text-zinc-100"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
                {policy?.name || 'Privacy Policy'}
              </h2>
              {policy?.updatedAt && (
                <p className="text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                  Last updated: {formatDate(policy.updatedAt)}
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-2xl flex-1 px-6 pt-32 pb-12">
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <div dangerouslySetInnerHTML={{ __html: policy?.content || '' }} />
        )}
      </main>
    </motion.div>
  );
}
